import { useAutoTranslations } from './useAutoTranslations.jsx';
/**
 * Hook providing translation handling logic for form-based multilingual content.
 *
 * Automatically:
 * - collects source language field values from the form
 * - sends them for translation
 * - maps translated values into target language fields
 *
 * Designed for CMS/admin multilingual forms following naming convention:
 *   {field}_{lang}
 *
 * Example:
 *   title_sk -> title_en
 *   content_sk -> content_en
 */
export function useTranslationHandler(form, fields = ['title', 'content']) {
  const { processContent, isLoading, isError } = useAutoTranslations();

  /**
   * Translates configured form fields from source language
   * into target language and updates form state.
   *
   * @param {string} sourceLang Source language code
   * @param {string} targetLang Target language code
   * @param {string} provider Translation provider identifier
   */
  const handleTranslation = async (
    sourceLang = "sk",
    targetLang = "en",
    provider = "deepl"
  ) => {
    const values = form.getFieldsValue(true);

    const texts = [];
    const keys = [];

    for (const field of fields) {
      const sourceKey = `${field}_${sourceLang}`;
      const targetKey = `${field}_${targetLang}`;

      const value = values[sourceKey];
      if (value === null || value === undefined) continue;

      texts.push(value);
      keys.push(targetKey);
    }

    if (texts.length === 0) return;

    /**
     * Process collected content through translation pipeline.
     */
    const results = await processContent(
      texts,
      targetLang.toUpperCase(),
      sourceLang.toUpperCase(),
      provider
    );


    if (!results) return;

    /**
     * Map translated results to corresponding target fields.
     */
    const updates = {};
    results.forEach((text, i) => {
      if (text !== null && text !== undefined) {
        updates[keys[i]] = text;
      }
    });

    if (Object.keys(updates).length > 0) {
      form.setFieldsValue(updates);
    }
  };

  return {
    handleTranslation,
    isLoading,
    isError,
  };
}
